// entityDetailsBulk — bulk fetch/push av entity_details för Lager C
// save/load-pipelinen. Wrappar Supabase-anropen så useDocuments.tsx
// inte behöver känna till tabell-shape.
//
// Använder bulk_upsert_entity_details-RPC:n (Lovable Lager C migration)
// som tar en hel array av entity_details i ett enda nätverksanrop.
// Fallback till parallel single-row upsert_entity_details om bulk-RPC:n
// returnerar 404 (gammal DB-version utan migration applicerad). Båda
// pathways går via callRpc-shim:en så vi slipper any-cast på call-site.

import { callRpc } from '@/lib/supabaseTyped';
import { supabase } from '@/integrations/supabase/client';
import type {
  SplitDetail,
  SplitDetailAttachment,
} from './workflowDetailsSplit';

const ATTACHMENTS_BUCKET = 'entity-attachments';

function sanitisePathSegment(input: string): string {
  // Replace any character that isn't alphanumeric, dot, dash or
  // underscore with `_`, then collapse runs of `.` (length ≥ 2) into
  // `_` so `..` can never appear in the storage path even when the
  // surrounding slashes were already stripped (`b/../1` → `b___1`).
  return input.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/\.{2,}/g, '_');
}

interface DetailsBulkRow {
  entity_id: string;
  entity_type: string;
  description_md: string | null;
  metadata: unknown;
}

interface BulkUpsertResult {
  success: boolean;
  count?: number;
  error?: string;
  entity_id?: string;
}

/**
 * Fetcha alla entity_details-rader för ett workflow_id. Returnerar
 * SplitDetail-array kompatibel med mergeFromStorage. Tom array vid fel
 * eller noll rader (read failar inte loaden).
 */
export async function fetchAllDetailsForWorkflow(
  workflowId: string,
): Promise<SplitDetail[]> {
  const { data, error } = await supabase
    .from('entity_details')
    .select('entity_id, entity_type, description_md, metadata')
    .eq('workflow_id', workflowId);
  if (error || !data) return [];
  const rows = data as unknown as DetailsBulkRow[];
  return rows
    .filter((r) => r.entity_type === 'block' || r.entity_type === 'group' || r.entity_type === 'connector')
    .map((r) => ({
      entity_id: r.entity_id,
      entity_type: r.entity_type as SplitDetail['entity_type'],
      description_md: r.description_md ?? '',
      metadata: (r.metadata && typeof r.metadata === 'object'
        ? r.metadata as Record<string, unknown>
        : {}),
    }));
}

/**
 * Detektera "missing function"-fel från PostgREST. När bulk-RPC:n inte
 * är applicerad returnerar Supabase ett 404 / 42883-fel som vi vill
 * fall back på, men ALLA andra fel (t.ex. forbidden, validation) ska
 * propageras så vi inte tyst maskerar buggar.
 */
function isMissingFunctionError(error: { message?: string } | null): boolean {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return msg.includes('not find') || msg.includes('does not exist') || msg.includes('42883');
}

/**
 * Push:a en uppsättning details till entity_details. Försöker bulk-RPC
 * först (1 round-trip för N entities). Faller tillbaka på parallel
 * upsert_entity_details om bulk inte finns. Fail-soft: failer loggas
 * men kastar inte (callern bryter inte saven på orphan-details).
 */
export async function pushDetailsForWorkflow(
  workflowId: string,
  details: SplitDetail[],
): Promise<void> {
  if (details.length === 0) return;

  const payload = details.map((d) => ({
    entity_id: d.entity_id,
    entity_type: d.entity_type,
    description_md: d.description_md,
    metadata: d.metadata,
  }));

  const bulk = await callRpc<BulkUpsertResult>('bulk_upsert_entity_details', {
    p_workflow_id: workflowId,
    p_details: payload,
  });

  // Bulk lyckades (status fält kan saknas i äldre Supabase-versioner;
  // räcker att error är null).
  if (!bulk.error) return;

  // Bulk-RPC saknas → fallback. Andra fel propageras som noop (vi har
  // bara warn-toleranse på callsite ändå).
  if (!isMissingFunctionError(bulk.error)) return;

  await Promise.all(
    details.map((d) =>
      callRpc('upsert_entity_details', {
        p_workflow_id: workflowId,
        p_entity_id: d.entity_id,
        p_entity_type: d.entity_type,
        p_description_md: d.description_md,
        p_metadata: d.metadata,
      }),
    ),
  );
}

interface RegisterResult {
  success: boolean;
  error?: string;
  attachment_id?: string;
}

/**
 * Upload long-form details (those over MAX_INLINE_DETAIL_BYTES) to the
 * entity-attachments storage bucket and register an entity_attachments
 * row pointing at each upload. Must run AFTER `pushDetailsForWorkflow`
 * because `register_entity_attachment` requires the parent
 * `entity_details` row (looked up by `(workflow_id, entity_id)`) to
 * already exist for FK + RLS purposes.
 *
 * Per-attachment failures are logged and isolated — one failed upload
 * does not block siblings, and the function never throws so the save
 * pipeline stays fail-soft (consistent with `pushDetailsForWorkflow`).
 * On RPC failure after a successful upload the storage object is
 * removed to avoid orphans (mirror of `useUploadAttachment`).
 */
export async function pushBucketAttachmentsForWorkflow(
  workflowId: string,
  attachments: SplitDetailAttachment[],
): Promise<{ uploaded: number; failed: number }> {
  if (attachments.length === 0) return { uploaded: 0, failed: 0 };

  const results = await Promise.allSettled(
    attachments.map((a) => uploadOne(workflowId, a)),
  );

  let uploaded = 0;
  let failed = 0;
  for (const r of results) {
    if (r.status === 'fulfilled') uploaded += 1;
    else failed += 1;
  }
  return { uploaded, failed };
}

async function uploadOne(
  workflowId: string,
  a: SplitDetailAttachment,
): Promise<void> {
  // Sanitise both segments — entity_id values are normally UUIDs but
  // the helper should refuse anything that could traverse outside the
  // workflow-id prefix the storage RLS policy uses.
  const cleanEntityId = sanitisePathSegment(a.entity_id);
  const cleanName = sanitisePathSegment(a.filename);
  const objectPath = `${workflowId}/${cleanEntityId}/${crypto.randomUUID()}-${cleanName}`;

  const blob = new Blob([a.body], { type: a.mime_type });

  const { error: uploadErr } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .upload(objectPath, blob, {
      contentType: a.mime_type,
      upsert: false,
    });
  if (uploadErr) throw uploadErr;

  const reg = await callRpc<RegisterResult>('register_entity_attachment', {
    p_workflow_id: workflowId,
    p_entity_id: a.entity_id,
    p_entity_type: a.entity_type,
    p_filename: a.filename,
    p_mime_type: a.mime_type,
    p_size_bytes: a.size_bytes,
    p_storage_path: objectPath,
  });

  const ok = !reg.error && reg.data?.success;
  if (!ok) {
    // Rollback the storage upload so we don't accumulate orphans.
    await supabase.storage.from(ATTACHMENTS_BUCKET).remove([objectPath]);
    throw new Error(reg.error?.message ?? reg.data?.error ?? 'register-failed');
  }
}
